import React from 'react';
import {ThreadButton} from './novaDsControls';

// How much weight an action inside a Nova answer carries, in the design system's own terms.
// 'primary' is the step the conversation is meant to take next (Reply to Nova); 'secondary' is
// the ordinary action beside it (Copy, Change agent, Try again); 'quiet' is the one that ends
// something (Discard, Dismiss), which is drawn as text so it never competes with the two above.
export type NovaCompactButtonTone = 'primary' | 'secondary' | 'quiet';

const EMPHASIS_FOR_TONE: Record<NovaCompactButtonTone, 'high' | 'medium' | 'low'> = {
	primary: 'high',
	secondary: 'medium',
	quiet: 'low',
};

// The design system's icon names this button accepts. Narrow on purpose: the thread uses four of
// them, and a free `string` here would let a typo through to a silently missing glyph.
export type NovaCompactButtonIcon = 'sparkle' | 'trash' | 'chevronDown' | 'chevronUp';

export interface NovaCompactButtonProps {
	onPress: () => void;
	tone?: NovaCompactButtonTone;
	children: React.ReactNode;
	disabled?: boolean;
	// Why the control is refused, when it is. Rendered as the native title so the reason is legible
	// on hover, the same way the row control states its own.
	title?: string;
	// Passed through because a caller may need to point something at this button - the agent
	// picker positions itself against its own trigger by element id.
	id?: string;
	// Lets a caller extend this button with styled(), for a control that needs its own proportions.
	className?: string;
	leftIcon?: NovaCompactButtonIcon;
	rightIcon?: NovaCompactButtonIcon;
	'data-cy'?: string;
}

// Every action that sits inside a Nova answer card - Copy, Reply to Nova, Change agent, Discard,
// Try again - is one design-system Button at one height. A row of controls acting on the same
// answer has no reason to be drawn in two different systems, and one hand-built among them reads
// as a different kind of control than the Copy beside it.
//
// Only the height is this feature's own (see novaDsControls): the design system ships a single
// button size, and three of those in a card would out-weigh the answer they act on. Emphasis,
// colour, focus, hover, pressed and disabled treatment are all the system's.
export const NovaCompactButton = (props: NovaCompactButtonProps): JSX.Element => (
	<ThreadButton
		emphasis={EMPHASIS_FOR_TONE[props.tone ?? 'secondary']}
		leftIcon={props.leftIcon}
		rightIcon={props.rightIcon}
		disabled={props.disabled}
		title={props.title}
		onPress={props.onPress}
		useButtonProps
		noMargin
		id={props.id}
		className={props.className}
		data-cy={props['data-cy']}
	>
		{props.children}
	</ThreadButton>
);
